import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {useEngagementId} from '../../../../../../../util/customHooks';
import {documentCategoryNames, documentTypes, extendedPagingOptions} from '../../../../../../../util/uiconstants';
import {labels} from '../../../../../../../util/utils';
import {getAllAresDocuments} from '../../../../../../../actions/Document/documentactions';
import {getDocumentValidations} from '../../../../../../../actions/ValidationControl/validationcontrolactions';
import {getAccounts} from '../../../../../../../actions/Account/accountactions';
import {getAccountsForALRAValidations} from '../../../../../../../selectors/Ares/accounts';
import AgGridWrapper from '../../../../../BaseComponentWrappers/AgGridWrapper';
import Pagination from '../../../../../../../components/Common/Pagination/Pagination';
import NoRowsOverlay from './NoRowsOverlay';
import AccountNameCellRenderer from './AccountNameCellRenderer';
import ValidationCountCellRenderer from './ValidationCountCellRenderer';

export default function ALRAValidations({selectedStEntityId}) {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const getDataRow = useSelector((state) => getAccountsForALRAValidations(state));
	const containerStyle = useMemo(() => ({width: '100%', height: '100%'}), []);
	const [searchStringOnEnter, setSearchStringOnEnter] = useState();

	const pageNumber = 1;
	const pageSize = extendedPagingOptions.options[0];

	const handleOnGridReady = useCallback((params) => {
		const grids = document.querySelectorAll(`section.AGGridWrapperSection`);
		if (grids && grids.length > 0) {
			grids.forEach((grid) => {
				grid.querySelector('.ag-body-viewport').classList.add('customScrollbar');
			});
			resizeGrid(params);

			window.addEventListener('resize', function() {
				resizeGrid(params);
			});
		}
	}, []);

	const resizeGrid = (params) => {
		setTimeout(function() {
			params.api.sizeColumnsToFit();
		}, 500);
	};

	const getValidations = (documents) => {
		let callArray = [];
		let documentsWithApprovals = documents
			.filter((d) => d.approvals.length > 0)
			.map((d) => ({id: d.id, documentTypeId: d.documentTypeId}));

		for (let i = 0; i < documentsWithApprovals.length; i += 30) {
			let batch = documentsWithApprovals.slice(i, i + 30);
			callArray.push(batch);
		}

		let dispatchPromises = callArray.map((batch) => dispatch(getDocumentValidations(engagementId, batch)));

		return Promise.all(dispatchPromises);
	};
	const fetch = useCallback(
		() =>
			Promise.all([
				dispatch(
					getAccounts(engagementId, {
						searchString: searchStringOnEnter,
						orderBy: 'accountName',
						includeRelatedRisks: true,
						includePspIndexes: true,
						includeAssertions: true,
						includeHelix: true,
						page: pageNumber,
						pageSize: pageSize,
						includePagination: true,
						includeRelatedDocuments: true
					})
				),
				dispatch(
					getAllAresDocuments(
						engagementId,
						undefined,
						documentTypes.ALRA_INDIVIDUAL,
						documentCategoryNames.canvasFormNavigationIndividual,
						undefined,
						100
					)
				)
			]).then((response) => {
				return getValidations(response[1].data);
			}),

		[dispatch, engagementId, pageNumber, pageSize, searchStringOnEnter]
	);
	const defaultColDef = useMemo(() => {
		return {
			minWidth: 100,
			sortable: false,
			resizable: false,
			suppressMenu: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true
		};
	}, []);
	const columnDefs = useMemo(
		() => [
			{
				field: 'accountName',
				headerName: `${labels.ALRAAccountDisclosure}`,
				cellRenderer: 'accountNameCellRenderer',
				minWidth: '200',
				hide: false
			},
			{
				field: 'id',
				headerName: `${labels.ALRAValidations}`,
				cellRenderer: 'validationCount',
				cellRendererParams: {
					pill: true
				},
				maxWidth: '750',
				hide: false,
				baseColumn: true
				// pinned: 'right'
			}
		],
		[]
	);

	const noRowsOverlayComponent = () => {
		return <span>{selectedStEntityId > 0 ? labels.StEntityNoRecords : labels.ALRANoRecords}</span>;
	};

	return (
		<>
			<StyledALRAValidations className="StyledALRAValidations">
				<section style={containerStyle} className="AGGridWrapperSection">
					<AgGridWrapper
						rowData={getDataRow}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						animateRows
						noRowsOverlayComponent={noRowsOverlayComponent}
						suppressRowClickSelection
						suppressCellFocus
						wrapHeaderText
						autoHeaderHeight
						suppressContextMenu
						onReady={handleOnGridReady}
						components={{
							accountNameCellRenderer: AccountNameCellRenderer,
							validationCount: ValidationCountCellRenderer
						}}
					/>
				</section>
			</StyledALRAValidations>
		</>
	);
}
const StyledPagination = styled(Pagination)`
	display: flex;
	justify-content: flex-end;
	margin-top: var(--px-24);
`;

const StyledALRAValidations = styled.section`
	.ValidationsSearchbox {
		margin-bottom: var(--px-24);
		.ValidationsSearchWrapper {
			width: var(--px-320);
			.motif-form-field {
				height: 100% !important;
			}
			.motif-input {
				border-radius: var(--px-8);
				height: var(--px-54) !important;
			}
		}
	}
	.AGGridWrapperSection {
		.ALRAValidationLoader {
			width: 100%;
			height: 100%;
			background: transparent;
		}
		.ag-ltr {
			.ag-row-group-indent-0 {
				.ag-group-value {
					width: 100%;
				}
			}
			.ag-row-group-indent-1 {
				padding-left: 0;
				width: 100%;
				.ag-group-value {
					width: 100%;
				}
			}
			.ag-row-group-leaf-indent {
				margin-left: var(--px-24);
			}
		}
		.ag-header {
			.ag-header-viewport {
				.ag-header-container {
					.ag-header-row {
						.ag-header-cell {
							font-size: var(--px-14);
							font-weight: 400;
							.ag-header-cell-text {
								font-size: var(--px-14);
								font-weight: 400;
							}
							&[col-id='accountName'] {
								&.ag-header-cell {
									padding: var(--px-5) var(--px-8) var(--px-5) var(--px-24);
								}
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='id'] {
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
						}
					}
				}
			}
		}
		.ag-body-viewport {
			.ag-full-width-container {
				.ag-row {
					&.ag-row-level-3 {
						padding: 0 var(--px-110) !important;
					}
				}
			}
			.ag-row {
				&:last-child {
					border-bottom: 0;
				}
				&.ag-row-level-0 {
					.ag-cell {
						&[col-id='accountName'] {
							.ag-cell-value {
								font-weight: 400;
								line-height: var(--px-24);
								color: var(--neutrals-700);
							}
						}
						&[col-id='id'] {
							display: flex;
						}
					}
				}
				.ag-cell {
					&[col-id='accountName'] {
						padding-left: var(--px-24);
						color: var(--neutrals-900);
						text-align: left;
						display: inline-flex;
						align-items: center;
						width: 100%;
						.ag-cell-wrapper {
							width: 100%;
							.ag-cell-value {
								width: 100%;
								.motif-tooltip-wrapper {
									display: flex;
									width: 100%;
									.motif-tooltip-trigger-wrap {
										width: auto;
										max-width: 100%;
									}
								}
							}
						}
					}
					.ag-cell-value {
						.assertion-item {
							display: inline-flex;
							justify-content: center;
							align-items: center;
							margin: 0 !important;
							.craBadgesWrapper {
								margin: 0 !important;
								.assertion {
									margin: 0 !important;
								}
							}
						}
					}
				}
			}
		}
	}
`;
